import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Tainan (台南), Taiwan's oldest city — the tainan package.
 *
 * East → west, roughly as the real city lies around its long east–west avenue:
 *   HSR Tainan station (高鐵台南站, out in Guiren) with the shuttle bus bay and a palm-lined plaza ·
 *   Chihkan Tower (赤崁樓: Fort Provintia, 1653, two pavilions over a pond, nine stone turtles) ·
 *   Shennong Street (神農街: Shuixian Temple, brick 騎樓 arcades, lanterns over the lane) ·
 *   Anping (安平): Fort Zeelandia's ramparts and white watchtower, the Tree House banyan eating the
 *   Tait & Co. warehouse, the old street with shrimp-cracker and candied-fruit shops ·
 *   south of the avenue: Guohua Street market (國華街: beef soup, danzai noodles, coffin bread),
 *   the vermilion-walled Confucius Temple (孔廟, 1665) and the Anping canal with its tour boats.
 *
 * Ground chars of this package: '&' stone slabs (temple and fort courtyards, quays; lighter granite on the
 * HSR plaza), '*' red-brick herringbone lanes, '^' solid walls (red-brick ramparts inside the fort, vermilion
 * temple walls elsewhere: coping on top, a painted face on the row below). 'K' rows are the fort's brick faces.
 * Big landmarks are drawn in 80 px slices over 'O' footprints (`big`), like hlcity.
 */
const W = 96, H = 56;
const b = new MapBuilder(W, H, 'g');

// --- Edges and the elevated HSR line along the east side -----------------------------------------
b.fill(0, 0, W, 1, 'h').fill(0, H - 1, W, 1, 'h').fill(0, 0, 1, H, 'h');
b.fill(92, 0, 4, H, 'T');
b.fill(90, 1, 2, H - 2, 's');

// --- The avenue (府前路 → 中山路) and two north–south streets --------------------------------------
b.fill(1, 22, 91, 1, 's').fill(1, 23, 91, 3, 'r').fill(1, 24, 91, 1, 'l').fill(1, 26, 91, 1, 's');
b.fill(30, 1, 1, 54, 's').fill(31, 1, 2, 54, 'r').fill(33, 1, 1, 54, 's');
b.fill(69, 1, 1, 54, 's').fill(70, 1, 3, 54, 'r').fill(71, 1, 1, 54, 'l').fill(73, 1, 1, 54, 's');
b.fill(30, 23, 4, 3, 'r').fill(69, 23, 5, 3, 'r');
// Zebra crossings.
for (const x of [10, 40, 60, 84]) b.fill(x, 23, 2, 3, '=');
b.fill(30, 19, 4, 2, '=').fill(30, 27, 4, 2, '=').fill(69, 19, 5, 2, '=').fill(69, 27, 5, 2, '=');

// --- East: HSR Tainan station, its plaza and the shuttle bay; a small park south of the avenue ----
b.fill(74, 1, 18, 21, '&');
b.fill(82, 6, 10, 8, 'O'); // the station hall (tn_hsrStation art)
b.fill(74, 27, 18, 3, 'Q'); // scooter park
b.fill(82, 30, 2, 25, 'p');
b.fill(76, 33, 5, 2, 'p').fill(84, 45, 6, 2, 'p');
b.fill(76, 38, 5, 5, '~'); // park pond
b.fill(87, 37, 3, 3, 'G').fill(75, 49, 4, 2, 'G');

// --- Chihkan Tower (赤崁樓) precinct: brick courtyard, pond, the two pavilions ----------------------
b.fill(46, 1, 23, 21, '&');
b.fill(46, 1, 1, 21, 'h').fill(68, 1, 1, 21, 'h').fill(47, 21, 21, 1, 'h').fill(55, 21, 5, 1, '&');
b.fill(54, 5, 10, 6, 'O'); // Haishen Temple + Wenchang Pavilion (tn_chihkan art)
b.fill(48, 13, 6, 6, '~'); // lotus pond
b.fill(47, 2, 7, 3, 'g').fill(62, 15, 6, 5, 'g');

// --- Shennong Street (神農街): Shuixian Temple square, arcaded shophouses, the lantern lane ---------
b.fill(34, 1, 12, 11, '&');
b.fill(36, 3, 8, 4, 'O'); // Shuixian Temple (tn_matsu variant 1)
b.fill(34, 12, 12, 4, 'O'); // shophouse row (tn_shophouse)
b.fill(34, 16, 12, 3, '*'); // the lane
b.fill(34, 19, 12, 3, '&');

// --- Anping (安平): Fort Zeelandia, the Tree House, the old street ----------------------------------
b.fill(2, 1, 16, 12, '&');
b.fill(2, 1, 16, 1, '^').fill(2, 2, 16, 1, 'K');
b.fill(2, 1, 1, 12, '^').fill(17, 1, 1, 12, '^');
b.fill(2, 11, 16, 1, '^').fill(2, 12, 16, 1, 'K');
b.fill(9, 11, 2, 2, '&'); // the gate
b.fill(7, 4, 4, 4, 'O'); // the white watchtower (tn_fortTower art)
b.fill(8, 13, 4, 3, '&'); // from the gate down to the old street
b.fill(1, 13, 7, 3, 'O').fill(12, 13, 8, 3, 'O'); // old-street shophouses
b.fill(1, 16, 29, 3, '*'); // 延平老街
b.fill(1, 19, 29, 3, '&');
b.fill(21, 5, 8, 6, 'O'); // Tree House (tn_treeHouse art)
b.fill(20, 11, 10, 2, 'p').fill(24, 13, 2, 3, 'p').fill(19, 1, 1, 10, 'p').fill(19, 1, 10, 1, 'p');
b.fill(28, 1, 1, 4, 'G');

// --- South of the avenue: harbour square, Guohua Street market, the Confucius Temple ---------------
b.fill(1, 27, 68, 1, 's');
// Anping harbour square with the Matsu temple.
b.fill(3, 29, 8, 4, 'O'); // 開台天后宮 (tn_matsu variant 0)
b.fill(2, 33, 10, 3, 'Q');
b.fill(14, 28, 2, 8, 'p').fill(16, 31, 12, 1, 'p');
b.fill(22, 28, 4, 2, 'G');
// Guohua Street market: shophouses, the lane, the stall strip.
b.fill(34, 28, 17, 3, 'O');
b.fill(34, 31, 17, 2, '*');
b.fill(34, 33, 17, 3, 'Q');
// The canal (安平運河) with stone quays; the north–south street crosses it on a bridge.
b.fill(1, 36, 49, 4, '&');
b.fill(1, 40, 49, 1, '&').fill(1, 41, 49, 5, '~').fill(1, 46, 49, 1, '&');
b.fill(30, 36, 4, 4, 'r').fill(30, 41, 4, 5, 'y').fill(30, 46, 4, 1, 'r');
b.fill(31, 36, 2, 11, 'r').fill(30, 36, 1, 1, 's').fill(33, 36, 1, 1, 's');
b.fill(30, 41, 4, 5, 'y');
// South bank: pavement, a lane of newer shophouses with scooters, a park.
b.fill(1, 47, 68, 1, 's');
b.fill(2, 48, 8, 3, 'O').fill(36, 48, 10, 3, 'O'); // shophouses (variants 2 and 3)
b.fill(1, 51, 29, 3, '*').fill(34, 51, 17, 3, '*');
b.fill(12, 48, 16, 3, 'g').fill(18, 48, 2, 3, 'p');
// Confucius Temple: vermilion walls (two '^' rows: coping + south face) around a stone courtyard with lawns.
b.fill(52, 28, 17, 25, '&');
b.fill(52, 28, 17, 2, '^');
b.fill(52, 28, 1, 25, '^').fill(68, 28, 1, 25, '^');
b.fill(52, 51, 17, 2, '^');
b.fill(59, 28, 3, 2, '&'); // the gate (全臺首學 over it)
b.fill(57, 33, 7, 5, 'O'); // 大成殿 (tn_confucius art)
b.fill(53, 31, 3, 6, 'g').fill(65, 31, 3, 6, 'g');
b.fill(53, 42, 4, 8, 'g').fill(64, 42, 4, 8, 'g');
b.fill(54, 44, 3, 3, '~'); // the half-moon pond (泮池)
b.fill(53, 53, 17, 1, 's');

// --- Decor helpers ---------------------------------------------------------------------------------
const SLICE = 80;
/** A landmark drawn in slices so it never pops out of view (variant = slice, h = total height). */
function big(kind: Decor['kind'], cx: number, bottom: number, w: number, h: number): Decor[] {
  const n = Math.max(1, Math.ceil((h - 90) / SLICE) + 1);
  return Array.from({ length: n }, (_, k) => ({ kind, x: cx, y: bottom - SLICE * k, w, h, variant: k }));
}
const mid = (tx: number, ty: number) => ({ x: px(tx) + 8, y: px(ty) + 14 });
const tree = (tx: number, ty: number, kind: 'tree' | 'cherry' | 'pine' | 'xy_banyan' = 'tree'): Decor => ({ kind, ...mid(tx, ty), solid: { x: px(tx) + 2, y: px(ty) + 8, w: 12, h: 6 } });
const palm = (tx: number, ty: number, v = 0): Decor => ({ kind: 'tk_palm', ...mid(tx, ty), variant: v, solid: { x: px(tx) + 4, y: px(ty) + 9, w: 8, h: 5 } });
const banyan = (tx: number, ty: number, v = 0): Decor => ({ kind: 'tn_banyan', ...mid(tx, ty), w: 96, variant: v, solid: { x: px(tx) + 2, y: px(ty) + 6, w: 12, h: 8 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', ...mid(tx, ty), solid: { x: px(tx) + 6, y: px(ty) + 10, w: 4, h: 3 } });
const oldLamp = (tx: number, ty: number): Decor => ({ kind: 'xm_oldLamp', ...mid(tx, ty), solid: { x: px(tx) + 6, y: px(ty) + 10, w: 4, h: 3 } });
const person = (tx: number, ty: number, v: number, dx = 8, dy = 12): Decor => ({ kind: 'tn_person', x: px(tx) + dx, y: px(ty) + dy, variant: v });
const crowd = (tx: number, ty: number, v: number, w = 56, h = 20): Decor => ({ kind: 'tn_crowd', x: px(tx) + 8, y: px(ty) + 12, w, h, variant: v });
const sit = (tx: number, ty: number, v: number): Decor => ({ kind: 'tn_benchSit', ...mid(tx, ty), w: 30, variant: v, solid: { x: px(tx) - 6, y: px(ty) + 4, w: 28, h: 8 } });
const scooters = (tx: number, ty: number, n: number, v = 0): Decor => ({ kind: 'xy_scooterRow', x: px(tx) + n * 8, y: px(ty) + 14, w: n * 16, variant: v, solid: { x: px(tx) + 2, y: px(ty) + 4, w: n * 16 - 4, h: 10 } });
/** Shophouse row over an 'O' footprint `tw` tiles wide whose bottom row is `ty`. */
const shops = (tx: number, ty: number, tw: number, v: number): Decor => ({ kind: 'tn_shophouse', x: px(tx) + px(tw) / 2, y: px(ty + 1), w: px(tw), variant: v });
const vsign = (tx: number, ty: number, v: number): Decor => ({ kind: 'tn_vsign', x: px(tx) + 4, y: px(ty), variant: v });
const streetSign = (tx: number, ty: number, v: number): Decor => ({ kind: 'tn_streetSign', ...mid(tx, ty), variant: v, solid: { x: px(tx) + 6, y: px(ty) + 10, w: 4, h: 3 } });
const pot = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_flowerPot', ...mid(tx, ty), w: 30, variant: v, solid: { x: px(tx) + 1, y: px(ty) + 8, w: 14, h: 6 } });
const lanterns = (tx: number, ty: number, tw: number): Decor => ({ kind: 'lanternString', x: px(tx), y: px(ty) + 8, w: px(tw) });

const decor: Decor[] = [
  // ---- HSR station and plaza -------------------------------------------------------------------
  ...big('tn_hsrStation', px(87), px(14), 176, 120),
  ...[3, 9, 15].map((ty) => palm(75, ty, ty % 2)),
  ...[5, 11, 17].map((ty) => palm(90, ty, (ty + 1) % 2)),
  lamp(78, 20), lamp(84, 20), lamp(89, 20), lamp(78, 3), lamp(78, 11),
  { kind: 'ck_flagpole', x: px(76) + 8, y: px(2) + 14, solid: { x: px(76) + 6, y: px(2) + 10, w: 4, h: 4 } },
  { kind: 'planter', ...mid(80, 16), solid: { x: px(79) + 2, y: px(16) + 6, w: 28, h: 8 } },
  { kind: 'planter', ...mid(90, 16), solid: { x: px(89) + 2, y: px(16) + 6, w: 28, h: 8 } },
  sit(80, 18, 1), sit(89, 18, 4),
  { kind: 'xy_vending', ...mid(81, 14), solid: { x: px(81) + 2, y: px(14) + 8, w: 12, h: 6 } },
  { kind: 'trashBin', ...mid(79, 14) },
  { kind: 'bikeRack', ...mid(76, 20), w: 32 },
  { kind: 'xy_bicycle', x: px(75) + 8, y: px(19) + 10, w: 20, variant: 1 },
  crowd(84, 16, 5 + 16 * 2 + 256 * 3, 70, 18), crowd(79, 8, 3 + 16 * 5 + 256 * 3, 40, 20), crowd(87, 19, 3 + 16 * 7, 50, 14),
  person(83, 11, 3 * 4 + 1 + 128 * 3), person(89, 9, 17 * 4 + 2), person(77, 7, 24 * 4 + 0 + 128 * 4),
  { kind: 'tn_queue', x: px(81) + 8, y: px(22) + 10, variant: 5 + 16 * 3 },
  { kind: 'tn_busStop', x: px(80) + 8, y: px(22) + 12, solid: { x: px(80) + 6, y: px(22) + 8, w: 4, h: 4 } },
  { kind: 'xy_bus', x: px(86), y: px(25) + 14, solid: { x: px(84) - 4, y: px(24) + 2, w: 72, h: 14 } },
  { kind: 'xy_taxi', x: px(76) + 8, y: px(23) + 12, solid: { x: px(75) + 6, y: px(23) + 2, w: 36, h: 10 } },
  { kind: 'xy_taxi', x: px(79) + 8, y: px(23) + 12, variant: 1, solid: { x: px(78) + 6, y: px(23) + 2, w: 36, h: 10 } },
  { kind: 'tn_pigeons2', x: px(86) + 8, y: px(13) + 8, w: 40, variant: 2 },
  { kind: 'tn_manhole', ...mid(82, 24) }, { kind: 'tn_laneArrow', ...mid(78, 25) }, { kind: 'tn_laneArrow', ...mid(88, 23), variant: 1 },
  { kind: 'tn_puddle', x: px(76) + 8, y: px(18) + 8, w: 22 },
  // Park south of the avenue: scooter park, pond, rental kiosk, benches.
  scooters(75, 29, 6, 0), scooters(83, 29, 5, 1), scooters(76, 28, 4, 2),
  ...[[75, 36], [80, 44], [88, 34], [89, 48], [76, 52], [88, 52]].map(([tx, ty], i) => (i % 2 ? tree(tx, ty, 'xy_banyan') : palm(tx, ty, i % 2))),
  { kind: 'xy_lotus', x: px(78) + 8, y: px(40) + 8, w: 60 },
  { kind: 'hc_egret', x: px(80) + 4, y: px(42) + 12, w: 20 },
  sit(85, 37, 2), sit(85, 50, 7),
  { kind: 'tn_parterre', x: px(88) + 8, y: px(39) + 14, w: 44, h: 26, variant: 0 },
  { kind: 'tn_parterre', x: px(76) + 8 + 8, y: px(50) + 14, w: 60, h: 26, variant: 3 },
  person(83, 40, 12 * 4 + 2 + 128 * 2), person(84, 47, 6 * 4 + 0), { kind: 'tn_stroller', x: px(86) + 8, y: px(31) + 12, w: -1 },
  { kind: 'tn_cat', x: px(79) + 8, y: px(31) + 10, variant: 3 },
  { kind: 'tn_cables', x: px(90) + 8, y: px(27) + 12, w: 128, variant: 1 },

  // ---- The avenue: lamps, banyan street trees, poles, crossings, traffic -------------------------
  ...[4, 20, 36, 52, 76].map((tx) => lamp(tx, 22)),
  ...[12, 28, 44, 64, 88].map((tx) => lamp(tx, 26)),
  ...[8, 16, 24, 48, 56, 64].map((tx): Decor => ({ kind: 'tn_streetTree', ...mid(tx, 22), variant: 1, solid: { x: px(tx) + 3, y: px(22) + 9, w: 10, h: 5 } })),
  ...[6, 38, 54, 80].map((tx): Decor => ({ kind: 'tn_streetTree', ...mid(tx, 26), variant: 0, solid: { x: px(tx) + 3, y: px(26) + 9, w: 10, h: 5 } })),
  ...[2, 18, 42, 58, 74].map((tx): Decor => ({ kind: 'tn_cables', x: px(tx) + 8, y: px(26) + 12, w: 128 + (tx % 3) * 16 })),
  { kind: 'trafficLight', ...mid(29, 22) }, { kind: 'trafficLight', ...mid(34, 26) }, { kind: 'trafficLight', ...mid(68, 22) }, { kind: 'trafficLight', ...mid(74, 26) },
  { kind: 'tn_walkers', x: px(11), y: px(26), w: 48, variant: 16 + 2 },
  { kind: 'tn_walkers', x: px(41), y: px(26), w: 48, variant: 16 + 5 },
  { kind: 'tn_walkers', x: px(61), y: px(26), w: 48, variant: 16 + 9 },
  { kind: 'parkedCar', x: px(14), y: px(23), variant: 1 }, { kind: 'parkedCar', x: px(46), y: px(25) - 2 }, { kind: 'parkedCar', x: px(64), y: px(23), variant: 1 },
  { kind: 'xy_bus', x: px(24) + 8, y: px(25) + 14, solid: { x: px(22) + 4, y: px(24) + 2, w: 72, h: 14 } },
  ...[[5, 23], [21, 25], [37, 23], [53, 25], [66, 25], [27, 23]].map(([tx, ty], i): Decor => ({ kind: 'scooter', x: px(tx) + 8, y: px(ty) + 12, variant: i % 2 })),
  { kind: 'tn_manhole', ...mid(32, 24) }, { kind: 'tn_manhole', ...mid(50, 25) }, { kind: 'tn_manhole', ...mid(71, 30) },
  { kind: 'tn_laneArrow', ...mid(18, 25) }, { kind: 'tn_laneArrow', ...mid(45, 23) }, { kind: 'tn_laneArrow', ...mid(71, 10) }, { kind: 'tn_laneArrow', ...mid(31, 14) },
  { kind: 'tn_puddle', x: px(33) + 8, y: px(30) + 8, w: 20 }, { kind: 'tn_leaves', x: px(50) + 8, y: px(22) + 10, w: 30, variant: 2 },
  streetSign(33, 22, 3), streetSign(69, 26, 3),
  // The north–south streets: scooters, a few parked cars, cables.
  ...[[31, 6], [32, 12], [71, 36], [70, 44], [72, 50], [32, 32]].map(([tx, ty], i): Decor => ({ kind: 'scooter', x: px(tx) + 8, y: px(ty) + 12, variant: (i + 1) % 2 })),
  { kind: 'parkedCar', x: px(70), y: px(5) }, { kind: 'parkedCar', x: px(70), y: px(14), variant: 1 },
  { kind: 'tn_cables', x: px(33) + 8, y: px(8) + 12, w: 112, variant: 1 },
  { kind: 'tn_cables', x: px(73) + 8, y: px(40) + 12, w: 128 },

  // ---- Chihkan Tower precinct -------------------------------------------------------------------
  ...big('tn_chihkan', px(59), px(11), 176, 128),
  { kind: 'tn_turtle', ...mid(56, 14), variant: 0 }, { kind: 'tn_turtle', ...mid(58, 14), variant: 1 }, { kind: 'tn_turtle', ...mid(60, 14), variant: 2 },
  { kind: 'tn_turtle', ...mid(62, 14), variant: 0 }, { kind: 'tn_turtle', ...mid(64, 14), variant: 1 },
  { kind: 'xy_lotus', x: px(51), y: px(16), w: 80 },
  { kind: 'xm_koi', x: px(50) + 8, y: px(16) + 8, w: 60 },
  { kind: 'tn_quayRail', x: px(51), y: px(13) + 2, w: 96, variant: 1 },
  { kind: 'tn_quayRail', x: px(51), y: px(19) + 14, w: 96, variant: 1 },
  tree(48, 3, 'xy_banyan'), tree(52, 3, 'tree'), tree(66, 3, 'cherry'), tree(63, 17, 'xy_banyan'), tree(67, 19, 'tree'),
  { kind: 'tn_fortWall', x: px(66) + 8, y: px(13) + 14, w: 56, variant: 1, solid: { x: px(65), y: px(13) + 6, w: 48, h: 8 } },
  oldLamp(49, 20), oldLamp(66, 20), oldLamp(49, 8), oldLamp(66, 8),
  { kind: 'tn_lion', ...mid(54, 21), variant: 0, solid: { x: px(54) + 3, y: px(21) + 8, w: 10, h: 6 } },
  { kind: 'tn_lion', ...mid(60, 21), variant: 1, solid: { x: px(60) + 3, y: px(21) + 8, w: 10, h: 6 } },
  pot(55, 12, 0), pot(63, 12, 2), pot(47, 10, 1),
  crowd(59, 17, 6 + 16 * 1 + 256 * 2, 70, 18), crowd(50, 7, 3 + 16 * 4 + 256 * 2, 40, 16),
  { kind: 'tn_tripod', x: px(57) + 8, y: px(19) + 10, variant: 1 },
  person(65, 10, 20 * 4 + 3), person(48, 19, 9 * 4 + 1 + 128 * 8), person(67, 14, 30 * 4 + 2),
  sit(50, 11, 3), sit(66, 11, 5),
  { kind: 'tn_pigeons2', x: px(56) + 8, y: px(18) + 6, w: 36, variant: 4 },
  { kind: 'trashBin', ...mid(61, 20) },
  { kind: 'hc_sitters', x: px(51) + 8, y: px(20) + 12, w: 36, variant: 1, solid: { x: px(51) - 7, y: px(20) + 2, w: 30, h: 10 } },

  // ---- Shennong Street --------------------------------------------------------------------------
  ...big('tn_matsu', px(40), px(7), 128, 96).map((d) => ({ ...d, variant: (d.variant ?? 0) + 8 })), // variant 8+ = Shuixian Temple
  { kind: 'tn_incense', ...mid(39, 8), w: 20, solid: { x: px(39) + 1, y: px(8) + 6, w: 14, h: 8 } },
  { kind: 'tn_lion', ...mid(36, 7), variant: 0, solid: { x: px(36) + 3, y: px(7) + 8, w: 10, h: 6 } },
  { kind: 'tn_lion', ...mid(43, 7), variant: 1, solid: { x: px(43) + 3, y: px(7) + 8, w: 10, h: 6 } },
  { kind: 'xm_chessTable', ...mid(35, 10), solid: { x: px(34) + 10, y: px(10) + 2, w: 28, h: 10 }, variant: 2 },
  tree(44, 9, 'xy_banyan'), pot(34, 8, 3),
  person(41, 9, 2 * 4 + 1), person(37, 10, 11 * 4 + 3 + 128 * 1),
  shops(34, 15, 12, 0),
  vsign(36, 16, 2), vsign(41, 16, 6), vsign(44, 16, 7),
  lanterns(34, 18, 6), lanterns(40, 18, 6), lanterns(34, 17, 12),
  { kind: 'tn_washing', x: px(38) + 8, y: px(16) + 2, w: 24 },
  { kind: 'tn_lanternShop', x: px(37) + 8, y: px(16) + 6, w: 40 },
  streetSign(34, 19, 0),
  { kind: 'xy_cafeTables', ...mid(43, 20), solid: { x: px(42) + 6, y: px(20) + 2, w: 28, h: 10 } },
  sit(39, 20, 6),
  { kind: 'tn_cat', x: px(45) + 4, y: px(19) + 2, variant: 8 + 2, w: -1 },
  { kind: 'hc_dog', x: px(41) + 8, y: px(18) + 8, w: 20 },
  pot(35, 20, 1), pot(45, 21, 2),
  ...[[35, 17], [43, 17]].map(([tx, ty], i): Decor => ({ kind: 'scooter', x: px(tx) + 8, y: px(ty) + 12, variant: i })),
  { kind: 'xy_bicycle', x: px(40) + 8, y: px(20) + 10, w: 20, variant: 2 },
  crowd(39, 17, 4 + 16 * 6 + 256 * 2, 90, 14),
  person(44, 18, 26 * 4 + 2 + 128 * 4), person(36, 18, 5 * 4 + 3 + 128 * 5),
  { kind: 'tn_cables', x: px(45) + 8, y: px(21) + 12, w: 96, variant: 1 },

  // ---- Anping: Fort Zeelandia ---------------------------------------------------------------------
  ...big('tn_fortTower', px(9), px(8), 80, 120),
  { kind: 'tn_gate', x: px(10), y: px(13), w: 64, variant: 1 },
  { kind: 'tn_cannon', ...mid(4, 9), variant: 0, solid: { x: px(4) + 1, y: px(9) + 8, w: 14, h: 6 } },
  { kind: 'tn_cannon', ...mid(14, 9), variant: 1, solid: { x: px(14) + 1, y: px(9) + 8, w: 14, h: 6 } },
  { kind: 'tn_cannon', ...mid(15, 4), variant: 0, solid: { x: px(15) + 1, y: px(4) + 8, w: 14, h: 6 } },
  { kind: 'ck_flagpole', x: px(13) + 8, y: px(3) + 14, solid: { x: px(13) + 6, y: px(3) + 10, w: 4, h: 4 } },
  tree(4, 4, 'xy_banyan'), tree(15, 7, 'tree'),
  crowd(11, 9, 4 + 16 * 8 + 256 * 2, 56, 16), person(5, 7, 14 * 4 + 1 + 128 * 8), person(12, 5, 29 * 4 + 0 + 128 * 2),
  sit(6, 10, 8),
  { kind: 'tn_fortWall', x: px(4) + 8, y: px(15) + 12, w: 72, variant: 0 },
  { kind: 'tn_fortWall', x: px(17) + 8, y: px(15) + 12, w: 72, variant: 2 },
  { kind: 'tn_fortWall', x: px(27) + 8, y: px(14) + 14, w: 56, variant: 0, solid: { x: px(26) + 2, y: px(14) + 6, w: 44, h: 8 } },
  // The Tree House.
  ...big('tn_treeHouse', px(25), px(11), 150, 110),
  { kind: 'tk_fern', ...mid(20, 7), variant: 0 }, { kind: 'tk_fern', ...mid(29, 8), variant: 2 }, { kind: 'tk_fern', ...mid(21, 12), variant: 1 },
  person(22, 12, 8 * 4 + 1 + 128 * 8), person(27, 12, 19 * 4 + 1 + 128 * 4),
  { kind: 'tn_boardwalk', x: px(25), y: px(12) + 14, w: 160 },
  tree(20, 2, 'cherry'), tree(28, 13, 'tree'), tree(21, 14, 'xy_banyan'),
  // The old street (延平老街): awnings, shoppers, snacks, scooters, laundry.
  shops(1, 15, 7, 1), shops(12, 15, 8, 1),
  vsign(3, 16, 4), vsign(14, 16, 8), vsign(18, 16, 10),
  lanterns(1, 18, 8), lanterns(12, 18, 8),
  { kind: 'tn_washing', x: px(6) + 8, y: px(16) + 2, w: 20 }, { kind: 'tn_washing', x: px(16) + 8, y: px(16) + 2, w: 24 },
  { kind: 'tn_crackerRack', ...mid(7, 20), w: 24, solid: { x: px(7) + 1, y: px(20) + 6, w: 14, h: 8 } },
  { kind: 'tn_jarRow', ...mid(17, 20), w: 36, solid: { x: px(17) - 8, y: px(20) + 6, w: 32, h: 8 } },
  crowd(10, 17, 6 + 16 * 2 + 256 * 1, 100, 14), crowd(22, 17, 4 + 16 * 9 + 256 * 1, 60, 14),
  person(2, 20, 7 * 4 + 0 + 128 * 5), person(25, 20, 15 * 4 + 2 + 128 * 1),
  { kind: 'tn_queue', x: px(5) + 8, y: px(20) + 4, variant: 3 + 16 * 4 + 256 * 2 },
  scooters(24, 20, 4, 1), { kind: 'xy_bicycle', x: px(21) + 8, y: px(20) + 10, w: 20, variant: 0 },
  streetSign(1, 19, 1),
  { kind: 'tn_cat', x: px(11) + 8, y: px(19) + 10, variant: 1 }, { kind: 'hc_dog', x: px(28) + 8, y: px(18) + 8, w: 20 },
  tree(29, 20, 'tree'), pot(12, 20, 0), pot(22, 20, 3),
  { kind: 'tn_cables', x: px(1) + 8, y: px(21) + 12, w: 160 },
  { kind: 'tn_cables', x: px(13) + 8, y: px(21) + 12, w: 160, variant: 1 },

  // ---- Harbour square and the canal -------------------------------------------------------------
  ...big('tn_matsu', px(7), px(33), 128, 96),
  { kind: 'tn_incense', ...mid(6, 34), w: 20, solid: { x: px(6) + 1, y: px(34) + 6, w: 14, h: 8 } },
  { kind: 'tn_lion', ...mid(2, 34), variant: 0, solid: { x: px(2) + 3, y: px(34) + 8, w: 10, h: 6 } },
  { kind: 'tn_lion', ...mid(11, 34), variant: 1, solid: { x: px(11) + 3, y: px(34) + 8, w: 10, h: 6 } },
  { kind: 'xm_stall', ...mid(24, 33), variant: 0, solid: { x: px(23) + 8, y: px(33) + 2, w: 32, h: 12 } }, // oyster omelette
  { kind: 'xm_stall', ...mid(19, 29), variant: 3, solid: { x: px(18) + 8, y: px(29) + 2, w: 32, h: 12 } }, // fruit
  crowd(12, 33, 4 + 16 * 15 + 256 * 1, 50, 14), pot(4, 28, 2), pot(27, 35, 1),
  { kind: 'tn_cat', x: px(12) + 8, y: px(29) + 10, variant: 8 + 4 },
  { kind: 'tn_pigeons2', x: px(20) + 8, y: px(35) + 6, w: 36, variant: 9 },
  { kind: 'tn_cables', x: px(29) + 8, y: px(35) + 12, w: 96, variant: 1 },
  palm(13, 29, 0), palm(20, 29, 1), palm(27, 29, 0), palm(28, 34, 1), tree(17, 33, 'xy_banyan'), tree(2, 28, 'tree'),
  sit(19, 34, 9), { kind: 'hc_sitters', x: px(26) + 8, y: px(38) + 12, w: 36, variant: 0, solid: { x: px(26) - 7, y: px(38) + 2, w: 30, h: 10 } },
  crowd(8, 35, 3 + 16 * 11 + 256 * 5, 50, 10), person(15, 30, 13 * 4 + 0), person(22, 35, 27 * 4 + 1 + 128 * 2),
  { kind: 'tn_parterre', x: px(24), y: px(29) + 14, w: 60, h: 24, variant: 1 },
  // Quays: railings, bollards, lamps, boats, anglers, egrets.
  ...[1, 9, 17, 25, 34, 42].map((tx): Decor => ({ kind: 'tn_quayRail', x: px(tx), y: px(40) + 14, w: px(tx === 25 ? 5 : 8) })),
  ...[1, 9, 17, 25, 34, 42].map((tx): Decor => ({ kind: 'tn_quayRail', x: px(tx), y: px(46) + 3, w: px(tx === 25 ? 5 : 8), variant: 2 })),
  { kind: 'tn_bridgeRail', x: px(30) - 2, y: px(41), h: px(5) }, { kind: 'tn_bridgeRail', x: px(34), y: px(41), h: px(5) },
  ...[4, 14, 24, 38, 46].map((tx) => oldLamp(tx, 39)),
  ...[6, 20, 40].map((tx) => oldLamp(tx, 47)),
  { kind: 'tn_boat', x: px(10), y: px(43) + 12, w: 56, variant: 0 },
  { kind: 'tn_boat', x: px(24), y: px(44) + 4, w: 56, variant: 1 },
  { kind: 'tn_boat', x: px(41), y: px(43) + 10, w: 56, variant: 0 },
  { kind: 'tn_boat', x: px(4), y: px(44) + 12, w: 40, variant: 2 },
  { kind: 'tn_glints', x: px(8), y: px(45) + 14, w: 128 }, { kind: 'tn_glints', x: px(26), y: px(45) + 14, w: 128 }, { kind: 'tn_glints', x: px(42), y: px(45) + 14, w: 112 },
  { kind: 'tk_fisherman', x: px(37) + 8, y: px(39) + 12, w: 60, variant: 1 },
  { kind: 'hc_egret', x: px(47) + 8, y: px(40) + 10, w: 20 }, { kind: 'hc_egret', x: px(16) + 8, y: px(46) + 10, w: 20 },
  sit(11, 38, 10), sit(44, 38, 11), { kind: 'trashBin', ...mid(29, 38) },
  { kind: 'tn_cyclists', x: px(1), y: px(37), w: px(28) },
  person(5, 38, 23 * 4 + 1 + 128 * 8), person(33, 39, 1 * 4 + 1), person(19, 38, 31 * 4 + 1 + 128 * 7), person(46, 47, 10 * 4 + 1 + 128 * 1),
  // South bank: newer shophouses, scooters, a small park with a pavilion.
  shops(2, 50, 8, 2), shops(36, 50, 10, 3),
  vsign(4, 51, 1), vsign(8, 51, 9), vsign(38, 51, 11), vsign(44, 51, 0),
  scooters(3, 52, 5, 0), scooters(37, 52, 6, 1), scooters(13, 50, 4, 2),
  { kind: 'tn_cables', x: px(1) + 8, y: px(53) + 12, w: 144 }, { kind: 'tn_cables', x: px(35) + 8, y: px(53) + 12, w: 144 },
  { kind: 'tn_streetTree', ...mid(24, 50), variant: 1, solid: { x: px(24) + 3, y: px(50) + 9, w: 10, h: 5 } },
  { kind: 'tn_streetTree', ...mid(28, 50), variant: 0, solid: { x: px(28) + 3, y: px(50) + 9, w: 10, h: 5 } },
  sit(14, 52, 12), sit(26, 52, 13),
  person(10, 52, 4 * 4 + 3), person(42, 52, 18 * 4 + 0 + 128 * 5), person(20, 49, 22 * 4 + 2),
  { kind: 'hc_dog', x: px(23) + 8, y: px(52) + 8, w: 20 },

  // ---- Guohua Street market ----------------------------------------------------------------------
  shops(34, 30, 17, 4),
  vsign(35, 31, 0), vsign(39, 31, 6), vsign(44, 31, 11), vsign(48, 31, 10),
  streetSign(34, 33, 2),
  { kind: 'tn_cables', x: px(50) + 8, y: px(32) + 12, w: 128, variant: 1 },
  { kind: 'tn_queue', x: px(36) + 8, y: px(33) + 2, variant: 3 + 16 * 2 + 256 * 2 },
  { kind: 'tn_queue', x: px(46) + 8, y: px(33) + 2, variant: 2 + 16 * 6 + 256 * 2 },
  { kind: 'foodTable', ...mid(38, 34), solid: { x: px(38) - 4, y: px(34) + 4, w: 24, h: 10 } },
  { kind: 'foodTable', ...mid(43, 34), solid: { x: px(43) - 4, y: px(34) + 4, w: 24, h: 10 } },
  { kind: 'stool', ...mid(37, 35) }, { kind: 'stool', ...mid(39, 35) }, { kind: 'stool', ...mid(42, 35) }, { kind: 'stool', ...mid(44, 35) },
  { kind: 'hc_sitters', x: px(48) + 8, y: px(35) + 12, w: 36, variant: 1, solid: { x: px(48) - 7, y: px(35) + 2, w: 30, h: 10 } },
  crowd(41, 32, 6 + 16 * 3 + 256 * 1, 110, 12), crowd(48, 32, 3 + 16 * 12, 40, 10),
  scooters(45, 32, 3, 1),
  { kind: 'xy_bicycle', x: px(50) + 8, y: px(34) + 10, w: 20, variant: 1 },
  { kind: 'tn_cat', x: px(49) + 8, y: px(33) + 10, variant: 0, w: -1 },
  { kind: 'tn_manhole', ...mid(40, 32) }, { kind: 'tn_puddle', x: px(47) + 8, y: px(31) + 8, w: 18 }, { kind: 'tn_leaves', x: px(36) + 8, y: px(35) + 8, w: 26, variant: 1 },
  { kind: 'trashBin', ...mid(50, 33) },
  person(35, 32, 3 * 4 + 2 + 128 * 5), person(49, 31, 25 * 4 + 3 + 128 * 1),

  // ---- Confucius Temple ---------------------------------------------------------------------------
  ...big('tn_confucius', px(60) + 8, px(38), 176, 112),
  { kind: 'tn_gate', x: px(60) + 8, y: px(30), w: 64, variant: 0 },
  { kind: 'tn_lion', ...mid(58, 31), variant: 0, solid: { x: px(58) + 3, y: px(31) + 8, w: 10, h: 6 } },
  { kind: 'tn_lion', ...mid(62, 31), variant: 1, solid: { x: px(62) + 3, y: px(31) + 8, w: 10, h: 6 } },
  { kind: 'tn_incense', ...mid(60, 41), w: 24, solid: { x: px(60) - 2, y: px(41) + 6, w: 20, h: 8 } },
  banyan(54, 33, 0), banyan(66, 33, 1), banyan(65, 44, 2), banyan(54, 49, 1),
  { kind: 'xy_lotus', x: px(55) + 8, y: px(45) + 8, w: 40 },
  { kind: 'tn_quayRail', x: px(54), y: px(43) + 14, w: 48, variant: 1 }, { kind: 'tn_quayRail', x: px(54), y: px(47) + 14, w: 48, variant: 1 },
  { kind: 'xm_chessTable', ...mid(59, 47), solid: { x: px(58) + 10, y: px(47) + 2, w: 28, h: 10 }, variant: 1 },
  sit(64, 40, 14), sit(56, 40, 15), sit(61, 50, 16),
  crowd(60, 41, 5 + 16 * 13 + 256 * 5, 60, 10), crowd(63, 47, 3 + 16 * 14, 40, 14),
  person(57, 36, 2 * 4 + 1 + 128 * 8), person(63, 36, 10 * 4 + 3), person(66, 50, 18 * 4 + 0), person(54, 38, 26 * 4 + 2),
  { kind: 'tn_tripod', x: px(62) + 8, y: px(42) + 10, variant: 0 },
  { kind: 'tn_pigeons2', x: px(58) + 8, y: px(44) + 6, w: 30, variant: 7 },
  { kind: 'tn_leaves', x: px(56) + 8, y: px(50) + 8, w: 40, variant: 3 }, { kind: 'tn_leaves', x: px(65) + 8, y: px(39) + 8, w: 30, variant: 4 },
  { kind: 'tn_plaque', ...mid(55, 30), solid: { x: px(55) + 2, y: px(30) + 8, w: 12, h: 6 } },
  { kind: 'tn_squirrel', x: px(66) + 8, y: px(33) + 4 },
  // Outside the walls: pavements, scooters, a tea stand.
  ...[[53, 27], [63, 27], [67, 53]].map(([tx, ty], i): Decor => ({ kind: 'scooter', x: px(tx) + 8, y: px(ty) + 12, variant: i % 2 })),
  { kind: 'tn_foodTruck', x: px(56) + 8, y: px(54) + 12, variant: 3, solid: { x: px(54) + 10, y: px(54), w: 52, h: 12 } },
  tree(51, 30, 'tree'), tree(51, 40, 'xy_banyan'), tree(51, 50, 'tree'),
];

// Trees and hedges along the hedge rows so the edges read as leafy, not flat.
for (let tx = 2; tx < 90; tx += 5) if (tx < 46 || tx > 68) decor.push({ kind: (tx / 5) % 3 ? 'tree' : 'xy_banyan', x: px(tx) + 8 + ((tx * 7) % 5), y: px(0) + 12 });
for (let tx = 2; tx < 90; tx += 6) decor.push({ kind: (tx / 6) % 2 ? 'tree' : 'pine', x: px(tx) + 8 + ((tx * 3) % 5), y: px(55) + 12 });

const lights: Light[] = [
  // Avenue and plaza lamps.
  ...[4, 20, 36, 52, 76].map((tx) => ({ x: px(tx) + 8, y: px(22), r: 46, color: '255,224,150' })),
  ...[12, 28, 44, 64, 88].map((tx) => ({ x: px(tx) + 8, y: px(26), r: 46, color: '255,224,150' })),
  ...[78, 84, 89].map((tx) => ({ x: px(tx) + 8, y: px(20), r: 48, color: '230,240,255' })),
  { x: px(78) + 8, y: px(3), r: 48, color: '230,240,255' }, { x: px(78) + 8, y: px(11), r: 48, color: '230,240,255' },
  { x: px(87), y: px(10), r: 110, color: '220,236,255' }, // station glass
  { x: px(80) + 8, y: px(22), r: 30, color: '255,200,120' }, // bus stop
  // Chihkan: eaves lit gold, old lamps.
  { x: px(59), y: px(7), r: 90, color: '255,200,120' },
  ...[[49, 20], [66, 20], [49, 8], [66, 8]].map(([tx, ty]) => ({ x: px(tx) + 8, y: px(ty), r: 40, color: '255,214,140' })),
  // Shennong Street: lanterns everywhere, the temple's glow.
  ...[35, 38, 41, 44].map((tx) => ({ x: px(tx) + 8, y: px(16), r: 44, color: '255,120,70' })),
  ...[36, 40, 43].map((tx) => ({ x: px(tx) + 8, y: px(18) + 8, r: 40, color: '255,150,80' })),
  { x: px(40), y: px(5), r: 70, color: '255,160,90' },
  { x: px(38) + 8, y: px(19), r: 44, color: '255,190,120' }, // lantern workshop
  // Anping: old street lanterns, the shops, the fort beacon, the tree house.
  ...[3, 7, 14, 18].map((tx) => ({ x: px(tx) + 8, y: px(17), r: 42, color: '255,140,80' })),
  { x: px(5) + 8, y: px(19), r: 44, color: '255,200,130' }, { x: px(15) + 8, y: px(19), r: 44, color: '255,200,130' },
  { x: px(9), y: px(2), r: 70, color: '255,240,200' },
  { x: px(10), y: px(12), r: 44, color: '255,224,150' },
  { x: px(25), y: px(10), r: 56, color: '160,255,170' },
  // Harbour and canal.
  { x: px(7), y: px(31), r: 70, color: '255,150,80' },
  ...[4, 14, 24, 38, 46].map((tx) => ({ x: px(tx) + 8, y: px(39), r: 40, color: '255,214,140' })),
  ...[6, 20, 40].map((tx) => ({ x: px(tx) + 8, y: px(47), r: 40, color: '255,214,140' })),
  { x: px(10), y: px(43), r: 36, color: '255,230,160' }, { x: px(41), y: px(43), r: 36, color: '255,230,160' },
  { x: px(24), y: px(33), r: 40, color: '255,200,150' },
  // Market: stall bulbs and signs.
  ...[36, 41, 46].map((tx) => ({ x: px(tx) + 8, y: px(33), r: 48, color: '255,210,130' })),
  { x: px(39) + 4, y: px(28), r: 36, color: '255,120,120' }, { x: px(48) + 4, y: px(28), r: 36, color: '120,220,220' },
  // Confucius: gate lanterns, the hall, the pond.
  { x: px(58) + 8, y: px(29), r: 40, color: '255,140,80' }, { x: px(62) + 8, y: px(29), r: 40, color: '255,140,80' },
  { x: px(60) + 8, y: px(35), r: 80, color: '255,190,120' },
  { x: px(56) + 8, y: px(54), r: 44, color: '255,224,150' },
  // South bank shop signs.
  { x: px(4) + 4, y: px(48), r: 36, color: '255,170,120' }, { x: px(44) + 4, y: px(48), r: 36, color: '255,120,120' },
];

export const tainan: MapDef = {
  id: 'tainan',
  w: W,
  h: H,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#b8382c', wallLight: '#cf4a3a', wainscot: '#7a2418', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    { key: 'tn_station', rect: { x: px(74), y: 0, w: px(22), h: px(H) } },
    { key: 'tn_chihkan', rect: { x: px(46), y: 0, w: px(23), h: px(22) } },
    { key: 'tn_shennong', rect: { x: px(34), y: 0, w: px(12), h: px(22) } },
    { key: 'tn_anping', rect: { x: 0, y: 0, w: px(30), h: px(22) } },
    { key: 'tn_market', rect: { x: px(34), y: px(27), w: px(17), h: px(9) } },
    { key: 'tn_confucius', rect: { x: px(51), y: px(27), w: px(18), h: px(29) } },
    { key: 'tn_canal', rect: { x: 0, y: px(27), w: px(51), h: px(29) } },
    { key: 'tn_city', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: { train: { x: px(86) + 8, y: px(17) + 8, facing: 'up' } },
};
